import { inject, Injectable } from '@angular/core';
import { Observable, forkJoin, map } from 'rxjs';
import {
  EventqSortParams,
  EventsInProcessResponse,
  EventsQueueResponse,
  PatrolListResponse,
  SiteStats,
} from '../../../core/models/api-contracts';
import { AgentDataService } from '../../../core/services/agent-data.service';
import { EventHandleService } from '../../../core/services/event-handle.service';

/** Events dashboard facade — loads typed mock/API data and applies client-side sort until backend sort is live. */
@Injectable({ providedIn: 'root' })
export class EventsDashboardService {
  private readonly data = inject(AgentDataService);
  private readonly eventHandles = inject(EventHandleService);

  getEventsQueue(sort?: EventqSortParams): Observable<EventsQueueResponse> {
    return forkJoin({
      queue: this.data.getEventsQueue(),
      handles: this.data.getEventHandles(),
    }).pipe(
      map(({ queue, handles }) => {
        const available = this.eventHandles.filterAvailableQueueItems(queue.items, handles.items);
        return this.sortQueueResponse(
          { totalCount: available.length, items: available },
          sort,
        );
      }),
    );
  }

  getPatrolList(sort?: EventqSortParams): Observable<PatrolListResponse> {
    return this.data
      .getPatrolList()
      .pipe(map((response) => this.sortPatrolResponse(response, sort)));
  }

  getEventsInProcess(sort?: EventqSortParams): Observable<EventsInProcessResponse> {
    return forkJoin({
      inProcess: this.data.getEventsInProcess(),
      handles: this.data.getEventHandles(),
    }).pipe(
      map(({ inProcess, handles }) => {
        const items = this.eventHandles.mergeInProcessRows(inProcess.items, handles.items);
        return this.sortInProcessResponse({ totalCount: items.length, items }, sort);
      }),
    );
  }

  getSiteStats(): Observable<SiteStats> {
    return this.data.getSiteStats();
  }

  private sortQueueResponse(
    response: EventsQueueResponse,
    sort?: EventqSortParams,
  ): EventsQueueResponse {
    if (!sort?.column) {
      return response;
    }
    const items = [...response.items].sort((a, b) =>
      this.compareValues(a[sort.column as keyof typeof a], b[sort.column as keyof typeof b], sort.direction),
    );
    return { ...response, items };
  }

  private sortPatrolResponse(response: PatrolListResponse, sort?: EventqSortParams): PatrolListResponse {
    if (!sort?.column) {
      return response;
    }
    const items = [...response.items].sort((a, b) =>
      this.compareValues(a[sort.column as keyof typeof a], b[sort.column as keyof typeof b], sort.direction),
    );
    return { ...response, items };
  }

  private sortInProcessResponse(
    response: EventsInProcessResponse,
    sort?: EventqSortParams,
  ): EventsInProcessResponse {
    if (!sort?.column) {
      return response;
    }
    const items = [...response.items].sort((a, b) =>
      this.compareValues(a[sort.column as keyof typeof a], b[sort.column as keyof typeof b], sort.direction),
    );
    return { ...response, items };
  }

  private compareValues(
    left: string | number | undefined,
    right: string | number | undefined,
    direction: 'asc' | 'desc' = 'asc',
  ): number {
    if (typeof left === 'number' && typeof right === 'number') {
      return direction === 'asc' ? left - right : right - left;
    }
    const result = String(left ?? '').localeCompare(String(right ?? ''));
    return direction === 'asc' ? result : -result;
  }
}
